import React, { useRef, useEffect } from 'react';
import * as Yup from 'yup';
import { Formik, Field } from 'formik';
import { Row, Col, Form, Button } from 'react-bootstrap';
import Select from 'react-select';
import { usePincodeLookup } from '../../hooks/usePincodeLookup';
import { useDispatch } from 'react-redux';
import { updateProfile } from '../../store/features/profileSlice';

// --- NEW IMPORTS FOR PHONE VALIDATION ---
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js';
import 'react-phone-number-input/style.css';
// ----------------------------------------

// --- Schema ---
const baseSchema = {
  email_id: Yup.string().trim().email('Invalid Email ID'),
  address: Yup.string().trim().required('Address is required').min(5, 'Too Short'),
  pincode: Yup.string()
    .trim()
    .required('Zip/Postal Code is required')
    .matches(/^[a-zA-Z0-9\s\-]{3,12}$/, 'Invalid Zip/Postal Code'),
  city: Yup.string().required('City is required'),
  state: Yup.string().trim().required('State is required'),
  
  // Updated Mobile Validation
  mobile_no: Yup.string()
    .required('Mobile number is required')
    .test('is-valid-phone', 'Invalid Phone number', (value) => 
      value ? isValidPhoneNumber(value) : false
    ),
};

const farmerSchema = Yup.object().shape({
  ...baseSchema,
  first_name: Yup.string().trim().required('First name is required'),
  last_name: Yup.string().trim().required('Last name is required'),
});

const fieldOfficerSchema = Yup.object().shape({
  ...baseSchema,
  name: Yup.string().trim().required('Name is required'),
});

// --- Helpers ---
function generateClassName(errors, touched, fieldName) {
  const hasError = errors[fieldName] && touched[fieldName];
  const isSuccess = touched[fieldName];
  return `form-control ${hasError ? 'border-2 border-danger' : isSuccess ? 'border-2 border-success' : ''}`;
}

const generatePhoneClassName = (errors, touched, fieldName) => {
  const hasError = errors[fieldName] && touched[fieldName];
  const isSuccess = touched[fieldName];
  return `d-flex align-items-center form-control ${
    hasError ? 'border-2 border-danger' : isSuccess ? 'border-2 border-success' : ''
  }`;
};

function UpdateProfile({ userData, setEditInfo, role }) {
  const dispatch = useDispatch();
  const formApiRef = useRef({});
  const { pincodeData, loading: pincodeLoading, lookupPincode, reset: resetPincode } = usePincodeLookup();

  // Pre-format the mobile number so PhoneInput recognizes Indian numbers stored without +91
  let initialMobile = userData.mobile_no || '';
  if (initialMobile && !initialMobile.startsWith('+') && initialMobile.length === 10) {
    initialMobile = '+91' + initialMobile;
  }

  const initialValues = {
    user_id: userData.user_id || userData.field_officer_id,
    name: userData.name || '',
    first_name: userData.first_name || '',
    last_name: userData.last_name || '',
    email_id: userData.email_id || '',
    address: userData.address || '',
    pincode: userData.pincode || '',
    city: userData.city || '',
    state: userData.state || '',
    mobile_no: initialMobile,
  };

  const validationSchema = role === 'FIELD_OFFICER' ? fieldOfficerSchema : farmerSchema;

  useEffect(() => {
    if (!pincodeData || !formApiRef.current.setFieldValue) return;
    const { setFieldValue } = formApiRef.current;
    
    // Auto-fill text boxes based on pincode lookup
    setFieldValue('state', pincodeData.state);
    setFieldValue('city', pincodeData.district);
  }, [pincodeData]);

  const handleSubmit = (values, { setSubmitting }) => {
    // --- Strip +91 from Indian numbers before sending to backend ---
    let formattedMobile = values.mobile_no;
    if (formattedMobile && formattedMobile.startsWith('+91')) {
      formattedMobile = formattedMobile.replace('+91', '');
    }

    const payload = {
      ...values,
      mobile_no: formattedMobile,
    };

    dispatch(updateProfile(payload))
      .unwrap()
      .then(() => {
        if (setEditInfo) setEditInfo(false);
      })
      .catch((err) => console.error(err))
      .finally(() => setSubmitting(false));
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        handleChange,
        handleSubmit,
        setFieldValue,
        setFieldTouched,
        errors,
        touched,
        isSubmitting,
      }) => {
        formApiRef.current = { setFieldValue };
        return (
        <Form onSubmit={handleSubmit} className="mt-3">
          <Row className="g-3">
            
            {role === 'FIELD_OFFICER' ? (
              <Col md={12}>
                <div className="form-floating">
                  <Field
                    type="text"
                    name="name"
                    className={generateClassName(errors, touched, 'name')}
                    placeholder="Full Name"
                  />
                  <Form.Label>Full Name</Form.Label>
                </div>
                {errors.name && touched.name && <div className="text-danger small">{errors.name}</div>}
              </Col>
            ) : (
              <>
                {/* First Name */}
                <Col md={6}>
                  <div className="form-floating">
                    <Field
                      type="text"
                      name="first_name"
                      className={generateClassName(errors, touched, 'first_name')}
                      placeholder="First Name"
                    />
                    <Form.Label>First Name</Form.Label>
                  </div>
                  {errors.first_name && touched.first_name && <div className="text-danger small">{errors.first_name}</div>}
                </Col>

                {/* Last Name */}
                <Col md={6}>
                  <div className="form-floating">
                    <Field
                      type="text"
                      name="last_name"
                      className={generateClassName(errors, touched, 'last_name')}
                      placeholder="Last Name"
                    />
                    <Form.Label>Last Name</Form.Label>
                  </div>
                  {errors.last_name && touched.last_name && <div className="text-danger small">{errors.last_name}</div>}
                </Col>
              </>
            )}

            {/* Email */}
            <Col md={12}>
              <div className="form-floating">
                <Field
                  type="email"
                  name="email_id"
                  className={generateClassName(errors, touched, 'email_id')}
                  placeholder="Email"
                />
                <Form.Label>Email</Form.Label>
              </div>
              {errors.email_id && touched.email_id && <div className="text-danger small">{errors.email_id}</div>}
            </Col>

            {/* Mobile */}
            <Col md={12}>
              <div className="mb-1">
                <Field name="mobile_no">
                  {({ field, form }) => (
                    <PhoneInput
                      international
                      defaultCountry="IN"
                      placeholder="Mobile No. *"
                      value={field.value}
                      onChange={(val) => {
                        form.setFieldValue(field.name, val);
                        form.setFieldTouched(field.name, true, false);
                      }}
                      onBlur={() => form.setFieldTouched(field.name, true)}
                      className={generatePhoneClassName(errors, touched, 'mobile_no')}
                    />
                  )}
                </Field>
              </div>
              {errors.mobile_no && touched.mobile_no && <div className="text-danger small mt-1">{errors.mobile_no}</div>}
            </Col>

            {/* Address */}
            <Col md={12}>
              <div className="form-floating">
                <Field
                  type="text"
                  name="address"
                  className={generateClassName(errors, touched, 'address')}
                  placeholder="Address"
                />
                <Form.Label>Address</Form.Label>
              </div>
              {errors.address && touched.address && <div className="text-danger small">{errors.address}</div>}
            </Col>

            {/* Pincode */}
            <Col md={12}>
              <div className="form-floating">
                <Field
                  type="text"
                  name="pincode"
                  className={generateClassName(errors, touched, 'pincode')}
                  placeholder="Zip / Postal Code"
                  onBlur={(e) => {
                    setFieldTouched('pincode', true);
                    const val = String(e.target.value).trim();
                    // Auto-lookup only if it's a 6-digit Indian pincode format
                    if (val.length === 6 && /^\d+$/.test(val)) {
                      lookupPincode(val);
                    }
                  }}
                  onChange={(e) => {
                    handleChange(e);
                    if (String(e.target.value).length !== 6) resetPincode();
                  }}
                />
                <Form.Label>Zip / Postal Code</Form.Label>
              </div>
              {pincodeLoading && <small className="text-muted">Looking up address...</small>}
              {errors.pincode && touched.pincode && <div className="text-danger small">{errors.pincode}</div>}
            </Col>

            {/* Address suggestions from pincode */}
            {pincodeData?.addressSuggestions?.length > 0 && (
              <Col md={12}>
                <Form.Label className="small text-muted mb-1">Suggested addresses (select to auto-fill)</Form.Label>
                <Select
                  isClearable
                  isSearchable
                  placeholder="Select address suggestion"
                  options={pincodeData.addressSuggestions}
                  onChange={(opt) => opt && setFieldValue('address', opt.value)}
                />
              </Col>
            )}

            {/* State (Converted to Text Field) */}
            <Col md={12}>
              <div className="form-floating">
                <Field
                  type="text"
                  name="state"
                  className={generateClassName(errors, touched, 'state')}
                  placeholder="State"
                />
                <Form.Label>State</Form.Label>
              </div>
              {errors.state && touched.state && <div className="text-danger small">{errors.state}</div>}
            </Col>

            {/* City (Converted to Text Field) */}
            <Col md={12}>
              <div className="form-floating">
                <Field
                  type="text"
                  name="city"
                  className={generateClassName(errors, touched, 'city')}
                  placeholder="City"
                />
                <Form.Label>City</Form.Label>
              </div>
              {errors.city && touched.city && <div className="text-danger small">{errors.city}</div>}
            </Col>
          </Row>

          <div className="mt-4 d-grid">
            <Button type="submit" variant="dark" disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Save Changes'}
            </Button>
          </div>
        </Form>
        );
      }}
    </Formik>
  );
}

export default UpdateProfile;